import React from 'react';
import styles from '../../styles/components/common/Loader.module.css';

/**
 * Modern Unified Loader Component
 * Supports loading, empty state, and error states
 * 
 * @param {Object} props
 * @param {string} props.type - Type: 'loading', 'empty', 'error', 'spinner', 'section'
 * @param {string} props.message - Main message to display
 * @param {string} props.description - Additional description text
 * @param {string} props.size - Size: 'small', 'medium', 'large'
 * @param {boolean} props.fullPage - Whether to take full page height
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Inline styles
 * @param {Function} props.action - Optional action button handler
 * @param {string} props.actionText - Text for action button
 */
const Loader = ({ 
    type = 'loading', 
    message = 'Loading...', 
    description = '',
    size = 'medium',
    fullPage = false,
    className = '',
    style = {},
    action = null,
    actionText = 'Try Again'
}) => {
    const getLoaderContent = () => {
        switch (type) {
            case 'empty':
                return (
                    <div className={styles.emptyContainer}>
                        <div className={styles.emptyIllustration}>
                            {/* Animated Empty State Illustration */}
                            <div className={styles.emptyCloud}>
                                <svg width="200" height="200" viewBox="0 0 200 200" fill="none">
                                    {/* Background circles */}
                                    <circle className={styles.emptyCircle1} cx="100" cy="100" r="80" fill="#F0F4FF" opacity="0.5"/>
                                    <circle className={styles.emptyCircle2} cx="100" cy="100" r="60" fill="#E0EBFF" opacity="0.6"/>
                                    
                                    {/* Empty box illustration */}
                                    <g className={styles.emptyBox}>
                                        <rect x="60" y="70" width="80" height="60" rx="8" fill="#ffffff" stroke="#a0a0a0" strokeWidth="2.5"/>
                                        <path d="M 60 95 L 100 110 L 140 95" stroke="#a0a0a0" strokeWidth="2.5" fill="none"/>
                                        <line x1="100" y1="70" x2="100" y2="110" stroke="#a0a0a0" strokeWidth="2.5" strokeDasharray="5,5"/>
                                    </g>
                                    
                                    {/* Floating particles */}
                                    <circle className={styles.emptyParticle1} cx="50" cy="60" r="4" fill="#c0c0c0" opacity="0.6"/>
                                    <circle className={styles.emptyParticle2} cx="150" cy="80" r="3" fill="#c0c0c0" opacity="0.6"/>
                                    <circle className={styles.emptyParticle3} cx="140" cy="130" r="3.5" fill="#c0c0c0" opacity="0.6"/>
                                    <circle className={styles.emptyParticle4} cx="70" cy="50" r="2.5" fill="#c0c0c0" opacity="0.6"/>
                                </svg>
                            </div>
                        </div>
                        <h2 className={styles.emptyTitle}>{message}</h2>
                        {description && <p className={styles.emptyDescription}>{description}</p>}
                        {action && (
                            <button onClick={action} className={styles.actionButton}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                                    <polyline points="9 22 9 12 15 12 15 22" />
                                </svg>
                                {actionText}
                            </button>
                        )}
                    </div>
                );
            
            case 'error':
                return (
                    <div className={styles.errorContainer}>
                        <div className={styles.errorIllustration}>
                            {/* Animated Error Illustration */}
                            <div className={styles.errorCloud}>
                                <svg width="200" height="200" viewBox="0 0 200 200" fill="none">
                                    {/* Background circles */}
                                    <circle className={styles.errorCircle1} cx="100" cy="100" r="80" fill="#FFE5E5" opacity="0.3"/>
                                    <circle className={styles.errorCircle2} cx="100" cy="100" r="60" fill="#FFD1D1" opacity="0.4"/>
                                    
                                    {/* Sad computer/screen illustration */}
                                    <rect x="50" y="60" width="100" height="70" rx="8" fill="#ffffff" stroke="#dc3545" strokeWidth="3"/>
                                    <rect x="58" y="68" width="84" height="50" rx="4" fill="#FFF5F5"/>
                                    
                                    {/* Screen with X */}
                                    <line className={styles.errorX1} x1="75" y1="80" x2="125" y2="105" stroke="#dc3545" strokeWidth="4" strokeLinecap="round"/>
                                    <line className={styles.errorX2} x1="125" y1="80" x2="75" y2="105" stroke="#dc3545" strokeWidth="4" strokeLinecap="round"/>
                                    
                                    {/* Computer stand */}
                                    <rect x="90" y="130" width="20" height="15" fill="#dc3545"/>
                                    <rect x="70" y="145" width="60" height="5" rx="2" fill="#dc3545"/>
                                    
                                    {/* Floating error icons */}
                                    <g className={styles.floatingIcon1}>
                                        <circle cx="40" cy="50" r="8" fill="#ff6b6b"/>
                                        <text x="40" y="55" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">!</text>
                                    </g>
                                    <g className={styles.floatingIcon2}>
                                        <circle cx="160" cy="70" r="8" fill="#ff6b6b"/>
                                        <text x="160" y="75" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">!</text>
                                    </g>
                                    <g className={styles.floatingIcon3}>
                                        <circle cx="150" cy="130" r="6" fill="#ff8787"/>
                                        <text x="150" y="134" textAnchor="middle" fill="white" fontSize="10" fontWeight="bold">?</text>
                                    </g>
                                </svg>
                            </div>
                        </div>
                        <h2 className={styles.errorTitle}>{message}</h2>
                        {description && <p className={styles.errorDescription}>{description}</p>}
                        {action && (
                            <button onClick={action} className={styles.actionButton}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                    <path d="M3 3v5h5" />
                                </svg>
                                {actionText}
                            </button>
                        )}
                    </div>
                );
            
            case 'spinner':
                return (
                    <div className={styles.spinnerContainer}>
                        <div className={`${styles.spinner} ${styles[size]}`}>
                            <div className={styles.spinnerRing}></div>
                            <div className={styles.spinnerRing}></div>
                            <div className={styles.spinnerRing}></div>
                        </div>
                        <p className={styles.message}>{message}</p>
                    </div>
                );
            
            case 'section':
                return (
                    <div className={styles.sectionContainer}>
                        <div className={`${styles.sectionSpinner} ${styles[size]}`}>
                            <div className={styles.dot}></div>
                            <div className={styles.dot}></div>
                            <div className={styles.dot}></div>
                        </div>
                    </div>
                );
            
            case 'loading':
            default:
                return (
                    <div className={styles.loadingContainer}>
                        <div className={styles.modernSpinner}>
                            <div className={styles.spinnerCircle}></div>
                            <div className={styles.spinnerCircle}></div>
                            <div className={styles.spinnerCircle}></div>
                        </div>
                        <h2 className={styles.loadingTitle}>{message}</h2>
                        {description && <p className={styles.loadingDescription}>{description}</p>}
                    </div>
                );
        }
    };

    const containerClass = [
        styles.loaderWrapper,
        fullPage ? styles.fullPage : '',
        styles[type],
        className
    ].filter(Boolean).join(' ');

    return (
        <div className={containerClass} style={style}>
            {getLoaderContent()}
        </div>
    );
};

export default Loader;
